import { useLazyQuery, useQuery } from "@apollo/client";
import PropTypes from "prop-types";
import React, { createContext, useEffect, useState } from "react";

import { FIRST_ORDER, ORDERS_BY_EMAIL, ORDER_BY_RECEIPT } from "../Api";
import { dateRangeOptions } from "../constants/index";
import { getEmailFromToken } from "../util/authHelpers";

export const AppContext = createContext({});
let initialLastDate = new Date();
initialLastDate.setMonth(initialLastDate.getMonth() - dateRangeOptions[0]);

let dateCap = new Date();
dateCap.setMonth(dateCap.getMonth() - dateRangeOptions[dateRangeOptions.length - 1]);

export const AppContextProvider = ({ children }) => {
	const [alertText, setAlertText] = useState("");
	const [ticketId, setTicketId] = useState(null);
	const [email, setEmail] = useState("");
	const [lastDate, setLastDate] = useState(initialLastDate);
	const [showAlert, setAlert] = useState(false);
	const [isActionSuccessful, setActionSuccessful] = useState(false);
	const [historyLoading, setHistoryLoading] = useState(false);
	const [updatedOrders, setUpdatedOrders] = useState([]);
	const [kountSessionId, setKountSessionId] = useState("");

	const [receiptNo, setReceiptNo] = useState(null);
	const [orderByReceiptFailureCount, setOrderByReceiptFailureCount] = useState(0);

	const {
		data: orderByReceiptData,
		error: orderByReceiptError,
		loading: orderByReceiptLoading,
		refetch: refetchOrderByReceiptQuery
	} = useQuery(ORDER_BY_RECEIPT, {
		variables: { receipt: receiptNo },
		skip: !receiptNo,
		fetchPolicy: "network-only",
		context: {
			maxRetries: 5,
			onRetry: setOrderByReceiptFailureCount
		}
	});

	const [
		getOrdersByEmail,
		{
			error: ordersHistoryError,
			data: ordersHistoryData,
			loading: ordersHistoryLoading,
			refetch: refetchOrderHistory,
			networkStatus: orderHistoryStatus
		}
	] = useLazyQuery(ORDERS_BY_EMAIL, {
		variables: {
			email: email,
			endDate: lastDate,
			page: 0,
			size: 100
		},
		notifyOnNetworkStatusChange: true,
		fetchPolicy: "network-only",
		nextFetchPolicy: "standby"
	});

	const [
		getFirstOrderByEmail,
		{ error: getFirstOrderError, data: getFirstOrderData, loading: getFirstOrderLoading }
	] = useLazyQuery(FIRST_ORDER, {
		variables: {
			email: email,
			endDate: dateCap
		},
		fetchPolicy: "network-only",
		nextFetchPolicy: "standby"
	});

	const getFirstOrder = async () => {
		getFirstOrderByEmail();
	};

	const getOrderByReceiptNo = (receipt, ticketId) => {
		if (receipt) {
			setReceiptNo(receipt);
		}
		setTicketId(ticketId);
	};

	const refetchOrderByReceiptNo = receipt => {
		if (!updatedOrders.includes(receipt)) setUpdatedOrders(updatedOrders.concat(receipt));
		if (receiptNo === receipt) {
			refetchOrderByReceiptQuery({ receipt });
		} else {
			setReceiptNo(receipt);
		}
	};

	const getEmail = async () => {
		const userEmail = await getEmailFromToken();
		setEmail(userEmail);
	};

	const getOrderHistory = async () => {
		try {
			refetchOrderHistory();
		} catch (TypeError) {
			getOrdersByEmail();
		}
	};

	const appContext = {
		getOrderHistory,
		refetchOrderHistory,
		ordersHistoryData,
		ordersHistoryLoading,
		historyLoading,
		ordersHistoryError,
		orderHistoryStatus,
		getOrderByReceiptNo,
		lastDate,
		setLastDate,
		email,
		ticketId,
		setTicketId,
		orderByReceiptData,
		orderByReceiptError,
		orderByReceiptLoading: orderByReceiptLoading && {
			failureCount: orderByReceiptFailureCount,
			retries: 5
		},
		refetchOrderByReceiptNo,
		alertText,
		setAlertText,
		showAlert,
		setAlert,
		isActionSuccessful,
		setActionSuccessful,
		getEmail,
		updatedOrders,
		setUpdatedOrders,
		kountSessionId,
		setKountSessionId,
		getFirstOrderError,
		getFirstOrderData,
		getFirstOrderLoading,
		getFirstOrder
	};

	useEffect(() => {
		setHistoryLoading(ordersHistoryLoading);
	}, [ordersHistoryLoading, setHistoryLoading]);

	return <AppContext.Provider value={appContext}>{children}</AppContext.Provider>;
};

AppContextProvider.propTypes = {
	children: PropTypes.node
};
